import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader } from "@/components/brand/Mascot";
import { errText, getCustomerToken, getMe, googleLogin, logoutApi, setCustomerToken, trackShare } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getCustomerToken() ? undefined : null);
  const refresh = useCallback(() => getMe().then(setUser).catch(() => { setCustomerToken(null); setUser(null); }), []);
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    if (getCustomerToken()) refresh();
  }, [refresh]);
  const signIn = useCallback((d) => { setCustomerToken(d.token); setUser(d.user); }, []);
  const signOut = useCallback(async () => { try { await logoutApi(); } catch { /* token already gone */ } setCustomerToken(null); setUser(null); }, []);
  const addPoints = useCallback((n) => n && setUser((u) => (u ? { ...u, points: (u.points || 0) + n } : u)), []);
  const value = useMemo(() => ({ user, setUser, refresh, signIn, signOut, addPoints }), [user, refresh, signIn, signOut, addPoints]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const pointsToast = (n, label = "") => n > 0 && toast.success(`+${n} pts${label ? ` · ${label}` : ""}`, { className: "!bg-[#D7FF5B]" });

export const useShareReward = () => {
  const { user, addPoints } = useAuth();
  return useCallback(async (kind, ref) => {
    if (!user) return;
    try { const r = await trackShare(kind, ref); if (r.awarded) { addPoints(r.awarded); pointsToast(r.awarded, "shared"); } } catch { /* sharing still worked */ }
  }, [user, addPoints]);
};

export const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const AuthCallback = () => {
  const done = useRef(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    googleLogin(sid)
      .then((d) => { signIn(d); if (d.new) pointsToast(d.user.points, "welcome to the crew"); navigate("/account", { replace: true, state: { user: d.user } }); })
      .catch((e) => { toast.error(errText(e)); navigate("/account", { replace: true }); });
  }, [navigate, signIn]);
  return <Loader label="Signing you in" />;
};
